// Account page: who is signed in, a few numbers about their data, and changing the password.
import { ArrowLeft, CheckCircle2, CircleAlert, KeyRound, ListChecks, LoaderCircle, LogOut, PauseCircle, Sparkles } from 'lucide-react';
import { useEffect, useId, useMemo, useState, type FormEvent } from 'react';
import type { AppUser } from '../../shared/api';
import { dayToDate } from '../../shared/days';
import { PasswordField } from '../components/PasswordField';
import {
  AuthError,
  PASSWORD_MAX_LENGTH,
  PASSWORD_MIN_LENGTH,
  authErrorMessage,
  changePassword,
  getAccount,
} from '../lib/authClient';
import { goBack } from '../lib/router';
import { useStore } from '../lib/store';

const fullDate = new Intl.DateTimeFormat('de-DE', { day: 'numeric', month: 'long', year: 'numeric' });

export function AccountView({ user, onSignOut }: { user: AppUser; onSignOut: () => void }) {
  const { habits, done, loaded } = useStore();
  const [createdAt, setCreatedAt] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    void getAccount().then((account) => {
      if (alive && account?.createdAt) setCreatedAt(account.createdAt);
    });
    return () => {
      alive = false;
    };
  }, []);

  const stats = useMemo(() => {
    let days = 0;
    for (const set of done.values()) days += set.size;
    const first = habits.reduce<string | null>((min, h) => (min === null || h.startDay < min ? h.startDay : min), null);
    return {
      active: habits.filter((h) => !h.archived).length,
      paused: habits.filter((h) => h.archived).length,
      days,
      first,
    };
  }, [habits, done]);

  const since = createdAt && !Number.isNaN(Date.parse(createdAt)) ? fullDate.format(new Date(createdAt)) : stats.first ? fullDate.format(dayToDate(stats.first)) : null;

  return (
    <div className="view">
      <div className="topbar">
        <button type="button" className="icon-btn icon-btn--glass" onClick={() => goBack('/habits')} aria-label="Zurück">
          <ArrowLeft size={20} />
        </button>
      </div>

      <header className="hero account__hero">
        <span className="avatar avatar--lg" aria-hidden="true">
          {(user.name || user.email).slice(0, 1).toUpperCase()}
        </span>
        <div className="hero__text">
          <h1 className="hero__title">{user.name || 'Dein Konto'}</h1>
          <p className="hero__meta account__email">{user.email}</p>
        </div>
      </header>

      <h2 className="section-title">Benutzerdaten</h2>
      <section className="settings glass">
        {user.name && (
          <div className="settings__row">
            <span className="settings__title">Name</span>
            <span className="account__value">{user.name}</span>
          </div>
        )}
        <div className="settings__row">
          <span className="settings__title">E-Mail-Adresse</span>
          <span className="account__value settings__email">{user.email}</span>
        </div>
        <div className="settings__row">
          <span className="settings__title">Anmeldung</span>
          <span className="account__value">E-Mail und Passwort</span>
        </div>
        {since && (
          <div className="settings__row">
            <span className="settings__title">Dabei seit</span>
            <span className="account__value">{since}</span>
          </div>
        )}
        <div className="settings__row">
          <span className="settings__title">Sitzung</span>
          <button type="button" className="btn btn--ghost btn--sm" onClick={onSignOut}>
            <LogOut size={16} /> Abmelden
          </button>
        </div>
      </section>

      {loaded && (
        <div className="stats account__stats">
          <div className="stat glass">
            <span className="stat__label">
              <ListChecks size={15} aria-hidden="true" /> Aktiv
            </span>
            <span className="stat__value">{stats.active}</span>
          </div>
          <div className="stat glass">
            <span className="stat__label">
              <PauseCircle size={15} aria-hidden="true" /> Pausiert
            </span>
            <span className="stat__value">{stats.paused}</span>
          </div>
          <div className="stat glass">
            <span className="stat__label">
              <Sparkles size={15} aria-hidden="true" /> Erledigt
            </span>
            <span className="stat__value">
              {stats.days}
              <span className="stat__unit">{stats.days === 1 ? 'Tag' : 'Tage'}</span>
            </span>
          </div>
        </div>
      )}

      <h2 className="section-title">Passwort ändern</h2>
      <ChangePasswordForm />
    </div>
  );
}

type FieldError = { text: string; field: 'current' | 'password' | 'repeat' | 'form' };

function ChangePasswordForm() {
  const errorId = useId();
  const [current, setCurrent] = useState('');
  const [password, setPassword] = useState('');
  const [repeat, setRepeat] = useState('');
  const [signOutOthers, setSignOutOthers] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<FieldError | null>(null);
  const [saved, setSaved] = useState<string | null>(null);

  const validate = (): FieldError | null => {
    if (!current) return { text: 'Gib dein aktuelles Passwort ein.', field: 'current' };
    if (password.length < PASSWORD_MIN_LENGTH) return { text: `Das neue Passwort muss mindestens ${PASSWORD_MIN_LENGTH} Zeichen lang sein.`, field: 'password' };
    if (password.length > PASSWORD_MAX_LENGTH) return { text: `Das neue Passwort darf höchstens ${PASSWORD_MAX_LENGTH} Zeichen lang sein.`, field: 'password' };
    if (password === current) return { text: 'Das neue Passwort muss sich vom aktuellen unterscheiden.', field: 'password' };
    if (password !== repeat) return { text: 'Die beiden neuen Passwörter stimmen nicht überein.', field: 'repeat' };
    return null;
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setSaved(null);
    const invalid = validate();
    if (invalid) {
      setError(invalid);
      return;
    }
    setError(null);
    setBusy(true);
    try {
      await changePassword(current, password, signOutOthers);
      setCurrent('');
      setPassword('');
      setRepeat('');
      const text = signOutOthers ? 'Passwort geändert. Andere Geräte wurden abgemeldet.' : 'Passwort geändert.';
      setSaved(text);
    } catch (err) {
      if (err instanceof AuthError && err.status === 401 && !err.upstreamCode.toUpperCase().includes('PASSWORD')) {
        setError({ text: 'Deine Sitzung ist abgelaufen. Bitte melde dich erneut an.', field: 'form' });
      } else if (err instanceof AuthError && err.code === 'invalid_credentials') {
        setError({ text: authErrorMessage(err, 'change-password'), field: 'current' });
      } else {
        const field = err instanceof AuthError && err.code.startsWith('password_') ? 'password' : 'form';
        setError({ text: authErrorMessage(err, 'change-password'), field });
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="account__password glass">
      <form className="form" onSubmit={submit} noValidate>
        <PasswordField
          label="Aktuelles Passwort"
          autoComplete="current-password"
          value={current}
          onChange={setCurrent}
          invalid={error?.field === 'current'}
          describedBy={error?.field === 'current' ? errorId : undefined}
        />
        <PasswordField
          label="Neues Passwort"
          autoComplete="new-password"
          value={password}
          onChange={setPassword}
          hint={`Mindestens ${PASSWORD_MIN_LENGTH} Zeichen.`}
          invalid={error?.field === 'password'}
          describedBy={error?.field === 'password' ? errorId : undefined}
        />
        <PasswordField
          label="Neues Passwort wiederholen"
          autoComplete="new-password"
          value={repeat}
          onChange={setRepeat}
          invalid={error?.field === 'repeat'}
          describedBy={error?.field === 'repeat' ? errorId : undefined}
        />
        <label className="check-row">
          <input type="checkbox" checked={signOutOthers} onChange={(event) => setSignOutOthers(event.target.checked)} />
          <span>Auf allen anderen Geräten abmelden</span>
        </label>
        {error && (
          <p id={errorId} className="inline-error" role="alert">
            <CircleAlert size={18} aria-hidden="true" />
            {error.text}
          </p>
        )}
        {saved && (
          <p className="inline-success" role="status">
            <CheckCircle2 size={18} aria-hidden="true" />
            {saved}
          </p>
        )}
        <button type="submit" className="btn btn--primary btn--block" disabled={busy}>
          {busy ? <LoaderCircle size={18} className="spin" aria-hidden="true" /> : <KeyRound size={18} aria-hidden="true" />}
          {busy ? 'Wird gespeichert …' : 'Passwort ändern'}
        </button>
      </form>
    </section>
  );
}
